'use client';

// Renders the tree from lib/markdown.ts as elements. There is no HTML string
// anywhere in this file, and no dangerouslySetInnerHTML — a reply is data being
// described, not markup being trusted.

import { createContext, Fragment, memo, useContext } from 'react';
import { parseMarkdown, type Block, type Inline } from '@/lib/markdown';

/**
 * How a reply opens a document, when the surrounding app knows how.
 *
 * Context rather than a prop because `Markdown` is memoised and rendered once
 * per turn: threading a handler down would mean passing it through every turn
 * in the list to reach the one link at the bottom. Null outside a provider, and
 * everything below degrades to what it did before.
 */
export const OpenDoc = createContext<((path: string) => void) | null>(null);

/**
 * A path inside the workspace, written the way a reply writes one.
 *
 * Deliberately narrow. It has to match `docs/console-parity.md` and must not
 * match prose, a URL, a shell fragment, or the many other things that show up
 * in backticks: no spaces, no scheme, no leading slash, and it must end `.md`.
 * Markdown only, because a document is the thing worth opening in a pane built
 * to read documents — a source file wants an editor, which this is not.
 */
const DOC_PATH = /^(?!\/)(?:[\w.-]+\/)*[\w.-]+\.md$/;

function isDocPath(path: string): boolean {
  return DOC_PATH.test(path) && !path.includes('..');
}

/**
 * A path in a reply, made openable.
 *
 * A button rather than an anchor, because it goes nowhere: it swaps what is in
 * the pane you are already looking at. Anything an anchor would give — a new
 * tab, a copyable URL — would be a promise this cannot keep, since the document
 * is not addressable.
 */
function DocLink({ path, children }: { path: string; children: React.ReactNode }) {
  const open = useContext(OpenDoc);
  if (!open) return <>{children}</>;
  return (
    <button type="button" className="doc-link" onClick={() => open(path)} title={`Open ${path}`}>
      {children}
    </button>
  );
}

function inlines(nodes: Inline[]) {
  return nodes.map((node, i) => {
    switch (node.kind) {
      case 'text':
        return node.text;
      case 'code':
        // A path in backticks is how a reply usually names a file — more often
        // than a markdown link, which is why this is here and not only on
        // `link` below.
        return isDocPath(node.text) ? (
          <DocLink key={i} path={node.text}>
            <code>{node.text}</code>
          </DocLink>
        ) : (
          <code key={i}>{node.text}</code>
        );
      case 'strong':
        return <strong key={i}>{inlines(node.children)}</strong>;
      case 'em':
        return <em key={i}>{inlines(node.children)}</em>;
      case 'del':
        return <del key={i}>{inlines(node.children)}</del>;
      case 'br':
        return <br key={i} />;
      case 'link':
        // A relative link to a document is a link into this machine, not out to
        // the web. Opening it in a tab is what it used to do, and it 404s —
        // there is no route that serves a repo path.
        if (isDocPath(node.href)) {
          return (
            <DocLink key={i} path={node.href}>
              {inlines(node.children)}
            </DocLink>
          );
        }
        // Someone reading a reply on their phone does not want a link replacing
        // the conversation they are in the middle of.
        return (
          <a key={i} href={node.href} target="_blank" rel="noreferrer noopener">
            {inlines(node.children)}
          </a>
        );
    }
  });
}

function blocks(list: Block[]) {
  return list.map((block, i) => {
    switch (block.kind) {
      case 'para':
        return <p key={i}>{inlines(block.inline)}</p>;

      case 'heading': {
        const H = `h${Math.min(block.level + 1, 6)}` as 'h2';
        // Shifted down one: the page has its own h1, and a reply's "# Title" is
        // a section of a message rather than the heading of the document.
        return <H key={i}>{inlines(block.inline)}</H>;
      }

      case 'code':
        return (
          <div className="md-code" key={i}>
            {block.lang && <div className="md-code-lang">{block.lang}</div>}
            <pre>
              <code>{block.text}</code>
            </pre>
          </div>
        );

      case 'list':
        return block.ordered ? (
          <ol key={i} start={block.start}>
            {block.items.map((item, j) => (
              <li key={j}>{blocks(item)}</li>
            ))}
          </ol>
        ) : (
          <ul key={i}>
            {block.items.map((item, j) => (
              <li key={j}>{blocks(item)}</li>
            ))}
          </ul>
        );

      case 'quote':
        return <blockquote key={i}>{blocks(block.blocks)}</blockquote>;

      case 'rule':
        return <hr key={i} />;

      case 'table':
        return (
          // Wrapped because a wide table has to be able to scroll on its own
          // without widening the whole conversation.
          <div className="md-table" key={i}>
            <table>
              <thead>
                <tr>
                  {block.head.map((cell, j) => (
                    <th key={j} style={block.align[j] ? { textAlign: block.align[j]! } : undefined}>
                      {inlines(cell)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {block.rows.map((row, j) => (
                  <tr key={j}>
                    {row.map((cell, k) => (
                      <td key={k} style={block.align[k] ? { textAlign: block.align[k]! } : undefined}>
                        {inlines(cell)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
    }
  });
}

/**
 * One reply, rendered.
 *
 * Memoised on the text because a streaming turn re-renders on every delta, and
 * the turns above it have not changed — see TurnView in page.tsx, which is what
 * keeps that from re-parsing the whole conversation sixty times a second.
 *
 * `streaming` puts the marker after the last block, wherever that happens to be,
 * so it sits at the end of the prose rather than on a line of its own. Which of
 * the two markers it is comes from `writing`: a cursor while prose is arriving,
 * a light hanging over the line while the turn is off doing something else. A
 * turn alternates between those all the way through, so the marker has to as
 * well — it stayed a cursor through every tool call once the first paragraph
 * had landed, which said "still typing" for minutes at a time when nothing was
 * being typed.
 */
export const Markdown = memo(function Markdown({
  text,
  streaming,
  writing,
  breaks = true,
}: {
  text: string;
  streaming?: boolean;
  /** Prose arriving right now, rather than the turn merely being open. */
  writing?: boolean;
  /** False for prose out of a file, where a newline is the editor's and not the
   *  author's — see `parseMarkdown`. */
  breaks?: boolean;
}) {
  const tree = parseMarkdown(text, breaks);
  const state = !streaming ? '' : writing ? ' is-writing' : ' is-thinking';
  return (
    <div className={`md${state}`}>
      {tree.length ? <Fragment>{blocks(tree)}</Fragment> : streaming && <span className="caret" />}
    </div>
  );
});
